import { useCallback, useState } from 'react';
import size from 'lodash/size';
import take from 'lodash/take';

import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../categories/categoriesTypes';
import {
  ChangeMaterialsFiltersFunc,
  FetchMaterialsFilters
} from '../../../../../../materials/materialsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { useMaterialsFilterCategories } from './hooks/useMaterialsFilterCategories';

import { ThreeDStockMaterialsFiltersCategoriesFilterItem } from './components/ThreeDStockMaterialsFiltersCategoriesFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { LoadingSkeleton } from '../../../../../../../helpers/LoadingSkeleton';

import { categoriesKeys, words } from '../../../../../../../locales/keys';

const INITIAL_CATEGORIES_COUNT = 6;

interface ThreeDStockMaterialsFiltersCategoriesFilterProps {
  materialsFilters: FetchMaterialsFilters;
  defaultMaterialCategoryIds?: CategoryID[];
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  changeCategory: (
    changeFilters?: FetchMaterialsFilters,
    removeFilters?: string[]
  ) => void;
}

function ThreeDStockMaterialsFiltersCategoriesFilter({
  materialsFilters,
  changeMaterialsFilters,
  defaultMaterialCategoryIds,
  checkedCategories,
  setCheckedCategories,
  changeCategory
}: ThreeDStockMaterialsFiltersCategoriesFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [minimized, setMinimized] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const toggleMinimize = useCallback<() => void>(() => {
    setMinimized((prevState) => !prevState);
  }, [setMinimized]);

  const {
    checkedCategoryIds,
    handleCheckedCategory,
    handleChangeCategory,
    listCategories,
    listCategoriesFetched
  } = useMaterialsFilterCategories({
    materialsFilters,
    checkedCategories,
    setCheckedCategories,
    changeMaterialsFilters,
    changeCategory,
    defaultMaterialCategoryIds
  });

  if (listCategoriesFetched && size(listCategories) === 0) {
    return null;
  }

  return (
    <LoadingSkeleton loaded={listCategoriesFetched}>
      <div className="p-4">
        <div className="flex flex-col gap-3">
          <ButtonHelper
            className="flex items-center gap-2 rounded-sm"
            dataGa="categories-filter"
            onClick={toggleIsOpen}
          >
            <span className="text-sm font-semibold text-left flex-1">
              <Translate id={categoriesKeys.plural} />
            </span>

            <Icon
              icon={
                isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP
              }
              className="h-5 w-5 text-gray-500"
            />
          </ButtonHelper>

          {isOpen && (
            <ul>
              {(minimized
                ? take(listCategories, INITIAL_CATEGORIES_COUNT)
                : listCategories
              )?.map((category, index) => (
                <ThreeDStockMaterialsFiltersCategoriesFilterItem
                  key={index}
                  checked={checkedCategoryIds?.includes(category.id)}
                  onCheckedCategory={handleCheckedCategory}
                  onChangeCategory={handleChangeCategory}
                  category={category}
                />
              ))}

              {size(listCategories) > INITIAL_CATEGORIES_COUNT && (
                <PureIconButtonHelper
                  className="p-0 space-x-1 flex gap-1 text-gray-900 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 leading-5 focus:ring-base text-current hover:underline transition-colors duration-150 ease-out"
                  i18nText={minimized ? words.showAll : words.showLess}
                  i18nTextClassName="text-sm underline"
                  onClick={toggleMinimize}
                  icon={
                    minimized
                      ? IconsEnum.CHEVRON_DOWN_SOLID
                      : IconsEnum.CHEVRON_UP
                  }
                  iconClassName="w-5 h-5"
                />
              )}
            </ul>
          )}
        </div>
      </div>
    </LoadingSkeleton>
  );
}

export default ThreeDStockMaterialsFiltersCategoriesFilter;
